import React, { type ReactElement } from 'react'
import { useNavigate } from 'react-router'
import { Divider, type Palette, Typography, useTheme } from '@mui/material'
import { ReactComponent as RightArrowIcon } from 'assets/rightArrowIcon.svg'
import { type Tender } from 'features/bids/bid.types'
import { formatDate } from 'utils/functions'
import { ProposalStatus, StatusInfoName, TenderStatus } from 'common/tenders/tenders.types'
import { getTopicColor, getTopicLabel } from 'common/tenders/tenders.functions'

import styles from './TenderCard.module.scss'

export interface StatusInfo {
  color: string
  name: string
  disabledText?: string
  tooltipText?: string
}

export function getStatusInfo(
  palette: Palette,
  tenderStatus?: TenderStatus,
  proposalStatus?: ProposalStatus,
  proposalDeadline?: string,
): StatusInfo {
  const now = new Date()

  if (tenderStatus === TenderStatus.canceled) {
    return {
      color: palette.charts.red200.main,
      name: StatusInfoName.CANCELLED,
      disabledText: 'Tender je poništen od strane konsultanta.',
      tooltipText: 'Tender je poništen od strane konsultanta.',
    }
  }

  if (
    (tenderStatus === TenderStatus.submitted && proposalStatus === ProposalStatus.NOT_SELECTED) ||
    proposalStatus === ProposalStatus.SELECTED
  ) {
    return {
      color: palette.charts.orange400.main,
      name: StatusInfoName.CHOOSING_PROPOSALS,
      tooltipText: 'Tender je završen, u toku je proces odabira izvođača.',
    }
  }
  if (proposalStatus === ProposalStatus.DRAFT && proposalDeadline && new Date(proposalDeadline) < now) {
    return {
      color: palette.charts.red200.main,
      name: StatusInfoName.DEADLINE_PASSED,
      disabledText: 'Rok za davanje ponude je istekao.',
      tooltipText: 'Rok za davanje ponude je istekao.',
    }
  }
  if (
    tenderStatus === TenderStatus.submitted &&
    (proposalStatus === ProposalStatus.DRAFT || !proposalStatus)
  ) {
    return {
      color: palette.blue100.main,
      name: StatusInfoName.DRAFT,
      tooltipText: 'Vaša ponuda je snimljena ali nije poslata.',
    }
  }
  if (proposalStatus === ProposalStatus.SUBMITTED) {
    return {
      color: palette.charts.purple100.main,
      name: StatusInfoName.PROPOSAL_SENT,
      disabledText: 'Vaša ponuda je poslata i čeka se kraj tendera.',
      tooltipText: 'Vaša ponuda je poslata i čeka se kraj tendera.',
    }
  }
  if (proposalStatus === ProposalStatus.SELECTION_CONFIRMED) {
    return {
      color: palette.charts.green200.main,
      name: StatusInfoName.PROPOSAL_ACCEPTED,
      disabledText:
        'Tender je završen, ČESTITAMO Vaša ponuda je prihvaćena. Molimo Vas kontaktirajte konsultanta za dalje korake.',
      tooltipText:
        'Tender je završen, ČESTITAMO Vaša ponuda je prihvaćena. Molimo Vas kontaktirajte konsultanta za dalje korake.',
    }
  }
  if (proposalStatus === ProposalStatus.NOT_SELECTED) {
    return {
      color: palette.charts.red100.main,
      name: StatusInfoName.PROPOSAL_NOT_ACCEPTED,
      disabledText: 'Tender je završen, nažalost Vaša ponuda je odbijena.',
      tooltipText: 'Tender je završen, nažalost Vaša ponuda je odbijena.',
    }
  }
  return { color: palette.white.main, name: StatusInfoName.UNKNOWN }
}

interface TenderCardProps {
  tender: Tender
  index: number
  proposalDeadline: string
}

const TenderCard: React.FC<TenderCardProps> = ({ tender, index, proposalDeadline }): ReactElement => {
  const { palette } = useTheme()
  const navigate = useNavigate()
  const { color, name, disabledText } = getStatusInfo(
    palette,
    tender?.status,
    tender.proposals?.[0]?.status,
    proposalDeadline,
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.index}>
          <Typography variant="title3" sx={{ color: palette.white.main, textAlign: 'center' }}>
            {index}
          </Typography>
        </div>
        <div className={styles.status}>
          <Typography variant="subtitle1" sx={{ color: palette.white.main, marginTop: '10px' }}>
            Status:{' '}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color,
              marginTop: '10px',
            }}
          >
            {name}
          </Typography>
        </div>
      </div>
      <Divider className={styles.divider} flexItem />
      <Typography
        variant="title1"
        sx={{ color: palette.white.main, width: '100%', paddingInline: '15px', wordWrap: 'break-word' }}
      >
        {tender.name}
      </Typography>
      <div className={styles.topics}>
        {tender.topics.map((topic, idx) => (
          <div key={topic.topicId} className={styles.topic}>
            <div className={styles.topicsHeader}>
              <Typography
                variant="title1"
                sx={{
                  color: getTopicColor(topic.type),
                  width: '100%',
                  paddingLeft: '15px',
                }}
              >
                {getTopicLabel(topic.type)}
              </Typography>
              <Typography
                variant="title1"
                sx={{
                  color: palette.white.main,
                  width: '100%',
                  paddingInline: '15px',
                  wordWrap: 'break-word',
                  minHeight: '45px',
                  maxHeight: '45px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {topic.name}
              </Typography>
            </div>
            {idx !== tender.topics.length - 1 && <Divider className={styles.topicDivider} flexItem />}
          </div>
        ))}
      </div>
      <Divider className={styles.divider} flexItem />
      {tender.status !== TenderStatus.canceled && (
        <div className={styles.footer}>
          <div className={styles.deadline}>
            <Typography variant="subtitle1">Rok za davanje ponude</Typography>
            <Typography variant="subtitle1" sx={{ color: palette.white.main }}>
              {formatDate(new Date(proposalDeadline))}
            </Typography>
          </div>
          <div
            className={styles.details}
            onClick={() => {
              navigate(`/ponuda/${String(tender?.id)}`, { state: { disabledText } })
            }}
          >
            <RightArrowIcon height="25px" width="25px" fill={palette.white.main} />
          </div>
        </div>
      )}
    </div>
  )
}

export default TenderCard
